/**
 * Puja categories, and the four axes every puja is classified on.
 *
 * **Categories and all four axes are managed here** (CSR-028 closed). Occasions, deities,
 * traditions and ritual languages were until now the only catalogue content nobody could edit —
 * adding a regional tradition meant a seed edit and a deploy, which is the state product rule 1
 * exists to prevent.
 *
 * **Occasions are the exception, and the screen says why.** `occasion.code` is a closed machine key:
 * the website has a URL per occasion and the app's entry point branches on it. They can be renamed,
 * reordered and retired; they cannot be created. That is a boundary, not a missing feature, so it
 * is stated rather than left as a button that fails.
 *
 * **Retiring is what this has instead of deleting.** The pujas already classified under a value keep
 * their rows and their pages; the value stops being offered. Deleting would orphan live content to
 * save one row — and the admin read shows retired values precisely so they can be restored.
 *
 * **The home tile grid is gone from this screen.** It had no endpoint, at any path, in any slice —
 * it was six hard-coded strings with a Publish button that only ever raised a toast. A screen that
 * appears to publish the first thing every devotee sees, and does not, is the single most
 * dangerous kind of fixture.
 *
 * Renaming a category rewrites filter chips and page titles at the next site build, which is
 * exactly why it is data and not a code change.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { ScopeAction, Section } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { can } from '../lib/rbac';
import { useCancellationPolicies } from '../lib/api/hooks/cancellationPolicies';
import {
  useAxisValues,
  useCategories,
  useCreateAxisValue,
  useCreateCategory,
  usePatchAxisValue,
  usePatchCategory,
  type TaxonomyAxis,
} from '../lib/api/hooks/catalogue';
import { DeityEntityPanel } from './DeityEntity';

/**
 * The four axes, in the order the filter UI shows them.
 *
 * `creatable: false` on occasions is not a UI preference — `occasion.code` is a closed machine key
 * the website's URLs depend on, and the server refuses creation with that as its reason.
 */
/* `one` names a single value — the add form asks for "Deity name", not "Deities name". */
const AXES: readonly { key: TaxonomyAxis; label: string; one: string; note: string; creatable: boolean }[] = [
  { key: 'occasions', label: 'Occasions', one: 'Occasion', note: 'When a ritual is performed', creatable: false },
  { key: 'deities', label: 'Deities', one: 'Deity', note: 'Who it is addressed to', creatable: true },
  { key: 'sampradayas', label: 'Traditions', one: 'Tradition', note: 'How the vidhi is performed', creatable: true },
  {
    key: 'ritual-languages',
    label: 'Ritual languages',
    one: 'Ritual language',
    note: 'How the ceremony will sound',
    creatable: true,
  },
];

export function Taxonomy() {
  const { scope, toast } = useConsole();
  const [nameEn, setNameEn] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [slug, setSlug] = useState('');
  const [iconKey, setIconKey] = useState('');

  const categories = useCategories();
  const create = useCreateCategory();
  const patch = usePatchCategory();
  const policies = useCancellationPolicies();

  /* The policy a category pins, by name (R127; 08-payments/10 §11). Names, not versions: a pin
     follows the name to its next published version. A name that so far exists only as a draft is
     offered but labelled, because the server accepts it and falls back to the temple's policy, then
     the platform's, until a version is published. Policies are finance's, so a content editor may
     be refused the list — every name already pinned is still an option, so the row never shows a
     blank where a pin is. */
  const drafts = new Set<string>();
  const named = new Set<string>();
  const versions = policies.data ?? [];
  for (const p of versions) named.add(p.name);
  for (const name of named) {
    if (versions.every((p) => p.name !== name || p.status === 'draft')) drafts.add(name);
  }
  for (const c of categories.data ?? []) if (c.cancellation_policy_name) named.add(c.cancellation_policy_name);
  const policyNames = [...named].sort();

  /* A new category goes at the end. Computed on every render — including the one where the list
     has not arrived — so the fallback is a real path rather than a branch nothing ever takes. */
  const nextPosition = (categories.data?.length ?? 0) + 1;

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignItems: 'start' }}>
      <Section title="Puja categories" note="How the catalogue is grouped, for a devotee and for SEO">
        {categories.isPending && <LoadingState message="Reading categories…" />}
        {categories.isError && (
          <ErrorState title="Could not read categories" cause={errorMessage(categories.error)} />
        )}
        {categories.isSuccess && categories.data.length === 0 && (
          <EmptyState icon="layers" title="No category yet" inline />
        )}
        {categories.data && categories.data.length > 0 && (
          <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
            {categories.data.map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  gap: 'var(--sp-sm)',
                  alignItems: 'center',
                  padding: 'var(--sp-sm) var(--sp-md)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  background: 'var(--c-surface2)',
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* Both scripts, side by side. The Devanagari is not a translation of the
                      transliteration — it is what a devotee reading in Hindi actually sees. */}
                  <div style={{ fontWeight: 500 }}>
                    {c.name_en} · {c.name_hi}
                  </div>
                  <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                    {c.slug} · position {c.sort_order}
                  </div>
                </div>
                <Select
                  ariaLabel="Cancellation policy"
                  value={c.cancellation_policy_name ?? ''}
                  options={policyNames}
                  placeholder="Default — the temple’s, else the platform’s"
                  optionLabel={(name) => (drafts.has(name) ? `${name} (draft only — falls back)` : name)}
                  disabled={!can(scope, 'catalogue_content')}
                  onChange={(name) =>
                    patch.mutate(
                      { categoryId: c.id, patch: { cancellation_policy_name: name || null } },
                      outcome(name ? `${c.name_en} now cancels under ${name}` : `${c.name_en} back on the default policy`),
                    )
                  }
                />
                <StatusChip tone={c.is_active ? 'success' : 'neutral'} label={c.is_active ? 'active' : 'retired'} />
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    patch.mutate(
                      { categoryId: c.id, patch: { is_active: !c.is_active } },
                      outcome(c.is_active ? `${c.name_en} retired` : `${c.name_en} restored`),
                    )
                  }
                >
                  {c.is_active ? 'Retire' : 'Restore'}
                </ScopeAction>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
          <Input label="Name (transliteration)" value={nameEn} onChange={setNameEn} density="admin" />
          <Input label="नाम (Devanagari)" value={nameHi} onChange={setNameHi} density="admin" />
          <Input label="Slug" value={slug} onChange={setSlug} density="admin" />
          <Input label="Icon key" value={iconKey} onChange={setIconKey} density="admin" />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!nameEn || !nameHi || !slug || !iconKey}
            onClick={() =>
              create.mutate(
                { name_en: nameEn, name_hi: nameHi, slug, icon_key: iconKey, sort_order: nextPosition },
                {
                  onSuccess: () => {
                    setNameEn('');
                    setNameHi('');
                    setSlug('');
                    setIconKey('');
                    toast({ tone: 'success', message: `${nameEn} added` });
                  },
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                },
              )
            }
          >
            Add category
          </ScopeAction>
        </div>
      </Section>

      {AXES.map((axis) => (
        <AxisSection key={axis.key} axis={axis} />
      ))}
    </div>
  );
}

/**
 * One axis, with its values and the form that adds to them.
 *
 * A component per axis rather than one loop over four: each has its own query, its own add form
 * and its own in-flight state, and sharing those across four sections is how a rename on one axis
 * ends up clearing the form on another.
 */
function AxisSection({
  axis,
}: {
  axis: { key: TaxonomyAxis; label: string; one: string; note: string; creatable: boolean };
}) {
  const { scope, toast } = useConsole();
  const [nameEn, setNameEn] = useState('');
  const [nameSecond, setNameSecond] = useState('');
  const [extra, setExtra] = useState('');
  /* Deities only: the row whose entity panel (CSR-124) is open. */
  const [entity, setEntity] = useState<number | null>(null);

  const values = useAxisValues(axis.key);
  const create = useCreateAxisValue();
  const patch = usePatchAxisValue();

  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  return (
    <Section title={axis.label} note={axis.note}>
      {values.isPending && <LoadingState message={`Reading ${axis.label.toLowerCase()}…`} />}
      {values.isError && (
        <ErrorState title={`Could not read ${axis.label.toLowerCase()}`} cause={errorMessage(values.error)} />
      )}
      {values.isSuccess && values.data.items.length === 0 && (
        <EmptyState icon="tag" title={`No ${axis.label.toLowerCase()} listed`} inline />
      )}
      {values.data && values.data.items.length > 0 && (
        <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
          {values.data.items.map((v) => (
            <div key={v.id}>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--sp-sm)',
                  alignItems: 'center',
                  padding: 'var(--sp-sm) var(--sp-md)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 'var(--r-tile)',
                  background: v.is_active ? 'var(--c-surface2)' : 'var(--c-sunk)',
                }}
              >
                <div style={{ flex: 1 }}>
                  {/* Both scripts. The second is `name_hi` on three axes and `name_native` on ritual
                      languages — the API normalises the field so this renders all four. */}
                  <div style={{ fontWeight: 500 }}>
                    {v.name_en} · {v.name_second}
                  </div>
                  <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                    {v.slug}
                    {v.extra ? ` · ${v.extra}` : ''}
                  </div>
                </div>
                <StatusChip tone={v.is_active ? 'success' : 'neutral'} label={v.is_active ? 'active' : 'retired'} />
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    patch.mutate(
                      { axis: axis.key, valueId: v.id, patch: { is_active: !v.is_active } },
                      outcome(v.is_active ? `${v.name_en} retired` : `${v.name_en} restored`),
                    )
                  }
                >
                  {v.is_active ? 'Retire' : 'Restore'}
                </ScopeAction>
                {axis.key === 'deities' && (
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    ariaLabel={entity === v.id ? `Close ${v.name_en}` : `Details of ${v.name_en}`}
                    onClick={() => setEntity(entity === v.id ? null : v.id)}
                  >
                    {entity === v.id ? 'Close' : 'Details'}
                  </ScopeAction>
                )}
              </div>
              {entity === v.id && <DeityEntityPanel id={v.id} />}
            </div>
          ))}
        </div>
      )}

      {axis.creatable ? (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
          <Input label={`${axis.one} name`} value={nameEn} onChange={setNameEn} density="admin" />
          <Input label="Name in its own script" value={nameSecond} onChange={setNameSecond} density="admin" />
          <Input label="Note" value={extra} onChange={setExtra} density="admin" />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!nameEn || !nameSecond}
            onClick={() =>
              create.mutate(
                {
                  axis: axis.key,
                  body: { name_en: nameEn, name_second: nameSecond, extra: extra || null, sort_order: 0, slug: null },
                },
                {
                  onSuccess: () => {
                    setNameEn('');
                    setNameSecond('');
                    setExtra('');
                    toast({ tone: 'success', message: `${nameEn} added` });
                  },
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                },
              )
            }
          >
            Add
          </ScopeAction>
        </div>
      ) : (
        /* Not a disabled Add button: the server refuses creation here for a reason worth reading,
           and a greyed control would read as broken software rather than as a boundary. */
        <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-ink3)', fontSize: 'var(--fs-admin-colhead)' }}>
          Occasions are a closed set — the code behind each one is part of the website’s URLs and
          the app’s entry point branches on it. Rename, reorder or retire an existing one.
        </p>
      )}
    </Section>
  );
}
